import React, { useState, useMemo } from 'react';
import { Package, CheckCircle, XCircle, Ban, Search } from 'lucide-react';
import DeliverySearchModal from './DeliverySearchModal';

function DeliveriesTable({ items }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDelivery, setSelectedDelivery] = useState(null);

  // Filter items based on search term
  const filteredItems = useMemo(() => {
    if (!items || items.length === 0) return [];
    
    if (!searchTerm.trim()) return items;
    
    const term = searchTerm.toLowerCase();
    return items.filter(item => {
      const id = String(item.id || '').toLowerCase();
      const product = String(item.product || '').toLowerCase();
      const recipient = String(item.recipient_name || '').toLowerCase();
      const phone = String(item.recipient_phone || '').toLowerCase();
      
      return id.includes(term) || 
             product.includes(term) || 
             recipient.includes(term) || 
             phone.includes(term);
    });
  }, [items, searchTerm]);

  function handleRowClick(item) {
    setSelectedDelivery(item);
  }

  function handleCloseModal() {
    setSelectedDelivery(null);
  }

  if (!items || items.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
        <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-500 font-montserrat">No hay entregas en este período</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-4">
          <h3 className="text-sm font-bold text-slate-900 font-montserrat flex items-center gap-2">
            <Package className="w-4 h-4 text-brand-primary" />
            Últimas Entregas ({filteredItems.length})
          </h3>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por ID, producto, nombre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat w-64"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <div className="max-h-96 overflow-y-auto">
            {filteredItems.length === 0 ? (
              <div className="p-8 text-center">
                <Search className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500 font-montserrat">
                  No se encontraron entregas con "{searchTerm}"
                </p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 font-montserrat whitespace-nowrap">
                      ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 font-montserrat whitespace-nowrap">
                      Producto
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 font-montserrat whitespace-nowrap">
                      Estado
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 font-montserrat whitespace-nowrap">
                      A Cobrar
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 font-montserrat whitespace-nowrap">
                      Cobrado
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 font-montserrat whitespace-nowrap">
                      Notas Conductor
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 font-montserrat whitespace-nowrap">
                      Fecha
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredItems.map((item, idx) => {
                    const truncateText = (text, maxLength = 12) => {
                      if (!text) return '—';
                      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
                    };

                    return (
                      <tr 
                        key={item.id ?? idx} 
                        onClick={() => handleRowClick(item)}
                        className="hover:bg-slate-50 transition-colors cursor-pointer active:bg-slate-100"
                      >
                        <td className="px-4 py-3 text-sm text-slate-900 font-montserrat font-medium whitespace-nowrap">
                          #{item.id ?? '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700 font-montserrat whitespace-nowrap">
                          <span title={item.product || ''}>
                            {truncateText(item.product, 12)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold font-montserrat ${
                            item.status === 'entregado' 
                              ? 'bg-[#67A43C]/10 text-[#67A43C]'
                              : item.status === 'rechazado'
                              ? 'bg-red-50 text-red-600'
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            {item.status === 'entregado' && <CheckCircle className="w-3 h-3" />}
                            {item.status === 'rechazado' && <XCircle className="w-3 h-3" />}
                            {item.status === 'cancelado' && <Ban className="w-3 h-3" />}
                            {item.status || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-900 font-montserrat font-medium whitespace-nowrap">
                          S/ {(item.charge ?? 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-900 font-montserrat font-medium whitespace-nowrap">
                          S/ {(item.charged ?? 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 font-montserrat whitespace-nowrap">
                          <span title={item.notes_driver || ''}>
                            {truncateText(item.notes_driver, 12)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 font-montserrat whitespace-nowrap">
                          {item.delivery_date ? new Date(item.delivery_date).toLocaleDateString('es-PE') : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      {selectedDelivery && (
        <DeliverySearchModal 
          delivery={selectedDelivery} 
          onClose={handleCloseModal} 
        />
      )}
    </>
  );
}

export default DeliveriesTable;